import { useNavigate } from "react-router-dom";
import { ShieldAlert, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { api, endImpersonation, getImpersonationCompany, isImpersonating } from "@/lib/api";

/**
 * Sticky banner shown at the top of every authenticated page while a
 * super_admin is impersonating a tenant ("Modo Suporte").
 *
 * Reads the impersonation context from useAuth().impersonation (server side)
 * and falls back to localStorage (so it renders before /auth/me resolves on
 * a freshly-impersonated session).
 */
export default function ImpersonationBanner() {
  const { impersonation, refresh } = useAuth();
  const navigate = useNavigate();

  const localActive = isImpersonating();
  const active = !!impersonation || localActive;
  if (!active) return null;

  const localCompany = getImpersonationCompany();
  const tradeName =
    localCompany?.trade_name ||
    localCompany?.legal_name ||
    impersonation?.company_id ||
    "tenant";

  const exit = async () => {
    // Best-effort: log the end of the session in the audit trail BEFORE we drop
    // the impersonation token. A failure here must not block the user from
    // leaving the support mode.
    try {
      await api.post("/admin/impersonate/end");
    } catch (err) {
      console.warn("Failed to log impersonation end", err);
    }
    endImpersonation();
    await refresh();
    navigate("/admin/companies");
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-30 bg-amber-500 text-stone-950 border-b border-amber-700/40"
    >
      <div className="flex items-center gap-3 px-6 py-2.5 max-w-[1600px] mx-auto">
        <ShieldAlert className="h-4 w-4 shrink-0" />
        <div className="text-sm font-body flex-1 min-w-0 truncate">
          <span className="font-display font-bold uppercase tracking-wider mr-2 text-xs">
            Modo Suporte
          </span>
          Você está acessando o painel de <strong>{tradeName}</strong> como
          super-admin. Toda ação é registrada em auditoria.
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={exit}
          className="bg-stone-950 text-amber-400 hover:bg-stone-900 border-stone-950"
          data-testid="impersonation-exit"
        >
          <LogOut className="h-3.5 w-3.5 mr-1.5" /> Sair do modo suporte
        </Button>
      </div>
    </div>
  );
}
